import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import authService from '../services/authService';
import { User, AuthContextType } from '../types';
import { AUTH_UNAUTHORIZED_EVENT } from '../utils/authSession';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on app start
    const currentUser = authService.getCurrentUser();
    const currentUserId = authService.getCurrentUserId();

    if (currentUser && currentUserId && authService.isAuthenticated()) {
      setUser(currentUser);
      setUserId(currentUserId);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const onUnauthorized = () => {
      setUser(null);
      setUserId(null);
    };
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    // Do not toggle `loading` here. PublicRoute/ProtectedRoute unmount children
    // while loading is true, which remounts LandingPage and wipes the login error.
    const response = await authService.login({ email, password });
    setUser(response.user);
    setUserId(authService.getCurrentUserId());
  };

  const register = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    acceptedTerms?: boolean,
  ): Promise<void> => {
    // We don't set loading here because it triggers a re-render of PublicRoute
    // which unmounts the LandingPage and clears the success message
    try {
      await authService.register({
        email,
        password,
        firstName,
        lastName,
        acceptedTerms,
      });
      // Do not set user here as registration now requires email confirmation
    } catch (error) {
      throw error;
    }
  };

  const logout = (): void => {
    authService.logout();
    setUser(null);
    setUserId(null);
  };

  const updateUser = (updatedUser: User): void => {
    authService.updateStoredUser({
      ...updatedUser,
      roles: updatedUser.roles ?? ['user'],
    });
    setUser(updatedUser);
  };

  const value: AuthContextType = {
    user,
    userId,
    login,
    register,
    logout,
    updateUser,
    loading,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};